import { measureBox } from './measureBox';

/**
 * LA COLONNE QU'ON CLIQUE DOIT ÊTRE CELLE QU'ON VOIT (fondatrice, 19/09/2026).
 *
 * « Dans le composant Multicolonnes, quand je change d'image et que je clique sur une colonne dans le
 * rail de gauche, c'est une AUTRE colonne qui s'ouvre dans le builder. »
 *
 * Ce n'était pas la sélection qui se trompait : `useRevealSelection` ne savait faire défiler QUE la
 * colonne d'aperçu, verticalement. Or dès que le carrousel est actif (Multicolonnes sur mobile, bande
 * d'images, liste de collections…), les cartes vivent dans une piste à défilement HORIZONTAL. La carte
 * sélectionnée restait donc hors cadre, à droite, et la vendeuse continuait de regarder la carte que le
 * carrousel montrait — avec, par-dessus, un cadre de sélection peint dans le vide.
 *
 * On amène donc aussi la carte dans sa piste, avec la même arithmétique que le moteur du carrousel
 * (`sliderInit.goTo`) : en RTL `scrollLeft` part de 0 au bord DROIT et devient négatif, donc un simple
 * `left - left` enverrait la piste arabe dans le mauvais sens.
 */

/** Marge d'erreur (px) — une carte à deux pixels près est « déjà en vue », on ne la bouge pas. */
const EDGE_TOLERANCE = 2;

/** Les conteneurs défilants entre `el` (exclu) et `stopAt` (exclu) — du plus proche au plus lointain. */
export function scrollableAncestors(el: Element, stopAt: Element | null): HTMLElement[] {
  const out: HTMLElement[] = [];
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== stopAt) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowX) || /(auto|scroll)/.test(style.overflowY)) {
      out.push(node);
    }
    node = node.parentElement;
  }
  return out;
}

/**
 * De combien `track.scrollLeft` doit bouger pour que `el` soit en vue — 0 s'il y est déjà, ou si la
 * piste ne défile pas. Quand il faut bouger, on cale le bord de DÉPART de la carte sur celui de la
 * piste : c'est ce que fait un clic sur une pastille du carrousel, donc la carte arrive là où l'œil
 * l'attend au lieu d'affleurer le bord opposé.
 */
export function horizontalRevealDelta(track: HTMLElement, el: Element): number {
  if (track.scrollWidth - track.clientWidth <= 1) return 0;
  const m = measureBox(el);
  if (!m) return 0;
  const t = track.getBoundingClientRect();
  // Deux lignes plutôt qu'une condition : le lecteur anti-texte-en-dur prend un `>` suivi d'un `<` sur
  // la même ligne pour du contenu entre balises, et signalerait cette comparaison comme une phrase.
  const startsBefore = m.l + EDGE_TOLERANCE < t.left;
  const endsAfter = m.r - EDGE_TOLERANCE > t.right;
  if (!startsBefore && !endsAfter) return 0;
  const rtl = getComputedStyle(track).direction === 'rtl';
  return rtl ? -(t.right - m.r) : m.l - t.left;
}

/**
 * Amène `el` en vue dans chacune des pistes horizontales qui le contiennent, jusqu'à `stopAt`.
 *
 * L'ordre des pistes est sans importance : l'écart se mesure entre la carte et SA piste, et faire
 * défiler une piste parente les déplace toutes les deux d'autant — l'écart intérieur ne bouge pas.
 */
export function revealInTracks(el: Element, stopAt: Element | null, smooth: boolean): void {
  for (const track of scrollableAncestors(el, stopAt)) {
    const left = horizontalRevealDelta(track, el);
    if (left === 0) continue;
    track.scrollBy({ left, behavior: smooth ? 'smooth' : 'auto' });
  }
}
